import { useActiveUserContext, useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import {
    DataRequestStatusEnum,
    ResearchTeamMemberAccessStatusEnum,
    StudyRequestStatusEnum,
} from '@vivli/features/data-requests/infrastructure/enum';
import { IDataRequest, IRequestedStudy } from '@vivli/features/data-requests/infrastructure/interface';
import { AssignedAppTypeEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';

export function useRequestedStudyPermissions() {
    const { user: activeUser, orgHasPermission, hasPermission } = useActiveUserContext();
    const { isAmr } = useAssignedAppTypeContext();

    const getResearchTeamMemberForCurrentUser = (dataRequest: IDataRequest) => {
        return dataRequest?.researchTeam?.researchTeamDetails?.find((rtd) => rtd.userId === activeUser.userId);
    };

    //this just checks whether the requested study IPD meets downloadable prerequisites -
    //either because it's a downloadable package (so it will eventually be downloadable
    //by requesters) or because IPD has already been provided for it (in which case
    //its owner would be able to download it). This doesn't mean the current user
    //will be able to download it - that is tested by userCanDownloadIpd
    const requestedStudyIpdIsDownloadable = (requestedStudy: IRequestedStudy) => {
        let isDownloadable = false;
        const hasDataPackageId = requestedStudy?.ipdDataPackageId !== null;
        if (hasDataPackageId) {
            isDownloadable =
                !!requestedStudy?.downloadableRequestedStudyIPDDataPackage &&
                requestedStudy.studyRequestStatus === StudyRequestStatusEnum.IPDProvided;
        }
        return isDownloadable;
    };

    const userIsInStudyOrg = (requestedStudy: IRequestedStudy) => {
        return activeUser?.orgMemberships.some((o) => o.orgId === requestedStudy.organizationId);
    };

    const userIsOrgAdmin = (requestedStudy: IRequestedStudy) => {
        return orgHasPermission(requestedStudy.organizationId, PermissionsEnum.ProviderActAdv);
    };

    const userIsOrgDataProvider = (requestedStudy: IRequestedStudy) => {
        return orgHasPermission(requestedStudy.organizationId, PermissionsEnum.DataContrib);
    };

    const userIsDataProviderOrgAdmin = (requestedStudy: IRequestedStudy) => {
        return userIsOrgDataProvider(requestedStudy) && userIsOrgAdmin(requestedStudy);
    };

    const userIsDatasetOwner = (requestedStudy: IRequestedStudy) => {
        return userIsOrgDataProvider(requestedStudy);
    };

    const userOwnsStudy = (requestedStudy: IRequestedStudy) => {
        const _isDataOwner = userIsDatasetOwner(requestedStudy);
        const _isDataProviderOrgAdmin = userIsDataProviderOrgAdmin(requestedStudy);
        return _isDataOwner || _isDataProviderOrgAdmin;
    };

    const userIsDuaValidated = (dataRequest: IDataRequest) => {
        if (!isAmr) {
            const teamMember = getResearchTeamMemberForCurrentUser(dataRequest);

            return teamMember?.accessToData === ResearchTeamMemberAccessStatusEnum.Granted;
        } else {
            //Amr has no research team members - user is always DUA validated
            //once data request DUA approval status has been reached
            let duaApprovalStage = false;
            switch (dataRequest.status) {
                case DataRequestStatusEnum.DUAValidated:
                case DataRequestStatusEnum.PartiallyFulfilled:
                case DataRequestStatusEnum.Fulfilled:
                    duaApprovalStage = true;
                    break;
            }
            return duaApprovalStage;
        }
    };

    const userCanDownloadIpd = (requestedStudy: IRequestedStudy, dataRequest: IDataRequest) => {
        const activeUserHasDownloadPermissions = isAmr
            ? true //anyone can download, even a Vivli Admin
            : !hasPermission(PermissionsEnum.DrActAdv);
        return (
            (requestedStudyIpdIsDownloadable(requestedStudy) &&
                activeUserHasDownloadPermissions && //2 cases: 1) a validated data requester for a downloadable study 2) the study's owner
                userIsDuaValidated(dataRequest) &&
                requestedStudy.downloadableRequestedStudyIPDDataPackage) ||
            userOwnsStudy(requestedStudy)
        );
    };

    return {
        userCanDownloadIpd,
        userIsInStudyOrg,
        userIsDuaValidated,
        requestedStudyIpdIsDownloadable,
        userIsOrgDataProvider,
        userIsDataProviderOrgAdmin,
        userIsDatasetOwner,
        userOwnsStudy,
    };
}
